/**
 * V1 Workflow Canvas — geometry-driven tree layout.
 *
 * Nodes are absolutely positioned using a recursive layout engine.
 * Edges are rendered in a single SVG overlay. No CSS connector fragments.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Menu,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconBell,
  IconBolt,
  IconClipboardList,
  IconGitBranch,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';

import type {
  ConditionGroup,
  LadderRung,
  RuleAction,
  RulePackDraft,
  TriggerConfig,
  WorkflowBranch,
  WorkflowStep,
} from '../../types';
import {
  ACTION_TYPES,
  CONDITION_FIELDS,
  CONDITION_OPERATORS,
  RULE_ACTION_KEYS,
  TRIGGER_EVENT_KINDS,
  TRIGGER_OPERATIONS,
  TRIGGER_RESOURCE_TYPES,
  defaultActionTypeForDomain,
} from '../../types';
import { makeEditorId } from '../../utils';
import { getConditionValueSelectOptions } from '../../utils/conditionFieldValueOptions';

import {
  appendToBranch,
  appendToElse,
  createActionStep,
  createBranchSplitStep,
  createInitialAlwaysRunUpdate,
  deepInsertAfter,
  deepRemoveStep,
  defaultConditionGroup,
  getWorkflowSteps,
  removeBranchOrSplit,
  setWorkflowSteps,
} from './workflowTree';

import {
  computeLayout,
  makeEdgePath,
  type LayoutRect,
} from './workflowLayout';

import styles from './WorkflowCanvas.module.css';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface Props {
  draft: RulePackDraft;
  onUpdate: (partial: Partial<RulePackDraft>) => void;
  onEditTrigger: () => void;
  onEditBranch: (branchId: string) => void;
  onEditAction: (actionId: string) => void;
}

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

export function WorkflowCanvas({
  draft,
  onUpdate,
  onEditTrigger,
  onEditBranch,
  onEditAction,
}: Props): JSX.Element {
  const workflowSteps = useMemo(() => getWorkflowSteps(draft), [draft]);
  const isPassthrough =
    draft.rungs.length === 1 &&
    !draft.steps?.length &&
    isAlwaysRunRung(draft.rungs[0]);

  const hasSteps = workflowSteps.length > 0 || isPassthrough;

  const commitSteps = useCallback(
    (newSteps: WorkflowStep[]) => onUpdate(setWorkflowSteps(newSteps)),
    [onUpdate],
  );

  // --- Mutation callbacks ---
  const handleAddActionAt = useCallback(
    (branchId: string, afterStepId: string | null) => {
      const s = createActionStep(defaultActionTypeForDomain(draft.domain));
      commitSteps(afterStepId ? deepInsertAfter(workflowSteps, afterStepId, s) : appendToBranch(workflowSteps, branchId, s));
    },
    [workflowSteps, commitSteps, draft.domain],
  );

  const handleAddBranchSplitAt = useCallback(
    (branchId: string, afterStepId: string | null) => {
      const s = createBranchSplitStep();
      commitSteps(afterStepId ? deepInsertAfter(workflowSteps, afterStepId, s) : appendToBranch(workflowSteps, branchId, s));
    },
    [workflowSteps, commitSteps],
  );

  const handleRemoveBranch = useCallback(
    (splitId: string, branchId: string) => commitSteps(removeBranchOrSplit(workflowSteps, splitId, branchId)),
    [workflowSteps, commitSteps],
  );

  const handleRemoveStep = useCallback(
    (stepId: string) => commitSteps(deepRemoveStep(workflowSteps, stepId)),
    [workflowSteps, commitSteps],
  );

  const handleAddElseAction = useCallback(
    (splitId: string) => commitSteps(appendToElse(workflowSteps, splitId, createActionStep(defaultActionTypeForDomain(draft.domain)))),
    [workflowSteps, commitSteps, draft.domain],
  );

  const handleAddElseBranch = useCallback(
    (splitId: string) => commitSteps(appendToElse(workflowSteps, splitId, createBranchSplitStep())),
    [workflowSteps, commitSteps],
  );

  const handleAddRootAction = useCallback(() => {
    const s = createActionStep(defaultActionTypeForDomain(draft.domain));
    const lastStep = workflowSteps[workflowSteps.length - 1];
    if (lastStep) {
      commitSteps(deepInsertAfter(workflowSteps, lastStep.id, s));
    } else {
      commitSteps([...workflowSteps, s]);
    }
  }, [workflowSteps, commitSteps, draft.domain]);

  const handleAddRootBranchSplit = useCallback(() => {
    const s = createBranchSplitStep();
    const lastStep = workflowSteps[workflowSteps.length - 1];
    if (lastStep) {
      commitSteps(deepInsertAfter(workflowSteps, lastStep.id, s));
    } else {
      commitSteps([...workflowSteps, s]);
    }
  }, [workflowSteps, commitSteps]);

  // Passthrough helpers
  const handlePassthroughAddAction = useCallback(() => {
    const step = createActionStep(defaultActionTypeForDomain(draft.domain));
    const action = (step as Extract<WorkflowStep, { kind: 'action' }>).action;
    const rung = draft.rungs[0];
    onUpdate({ rungs: [{ ...rung, actions: [...rung.actions, action] }] });
  }, [draft.rungs, draft.domain, onUpdate]);

  const handlePassthroughRemoveAction = useCallback(
    (actionId: string) => {
      const rung = draft.rungs[0];
      onUpdate({ rungs: [{ ...rung, actions: rung.actions.filter((a) => a.id !== actionId) }] });
    },
    [draft.rungs, onUpdate],
  );

  const handleSplitPassthrough = useCallback(() => {
    if (draft.rungs.length !== 1) return;
    const rung = draft.rungs[0];
    const actionSteps: WorkflowStep[] = rung.actions.map((a) => ({ id: makeEditorId('ws'), kind: 'action' as const, action: a }));
    const split = createBranchSplitStep();
    onUpdate(setWorkflowSteps([...actionSteps, split]));
  }, [draft.rungs, onUpdate]);

  const handleConvertToConditional = useCallback(() => {
    if (draft.rungs.length === 1) {
      const rung = draft.rungs[0];
      onUpdate({ rungs: [{ ...rung, name: rung.name || 'Branch 1', alwaysRun: false, conditionGroups: [defaultConditionGroup()] }] });
    }
  }, [draft.rungs, onUpdate]);

  // --- Compute layout ---
  const showTree = hasSteps && !isPassthrough && workflowSteps.length > 0;
  const layout = useMemo(() => showTree ? computeLayout(workflowSteps, true, draft.trigger) : null, [workflowSteps, showTree, draft.trigger]);

  // --- Canvas sizing (re-measure on resize for centering) ---
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setContainerW(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Offset to center tree inside container
  const treeOffsetX = layout ? Math.max(0, (containerW - layout.width) / 2) : 0;

  // --- Render ---
  if (!hasSteps) {
    return (
      <div className={styles.canvas} ref={containerRef}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <TriggerNode trigger={draft.trigger} onClick={onEditTrigger} />
          <div style={{ width: 2, height: 40, background: 'var(--mantine-color-gray-4)' }} />
          <AddFlowPlaceholder
            onAddActions={() => onUpdate(createInitialAlwaysRunUpdate(draft.domain))}
            onAddConditional={handleAddRootBranchSplit}
          />
        </div>
      </div>
    );
  }

  if (isPassthrough) {
    return (
      <div className={styles.canvas} ref={containerRef}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <TriggerNode trigger={draft.trigger} onClick={onEditTrigger} />
          <div style={{ width: 2, height: 40, background: 'var(--mantine-color-gray-4)' }} />
          <PassthroughLane
            rung={draft.rungs[0]}
            onEditAction={onEditAction}
            onAddAction={handlePassthroughAddAction}
            onAddBranch={handleSplitPassthrough}
            onRemoveAction={handlePassthroughRemoveAction}
            onAddConditions={handleConvertToConditional}
          />
        </div>
      </div>
    );
  }

  if (!layout) return <div className={styles.canvas} ref={containerRef} />;

  const PAD_BOTTOM = 60;

  return (
    <div className={styles.canvas} ref={containerRef}>
      <div
        style={{
          position: 'relative',
          width: layout.width,
          height: layout.height + PAD_BOTTOM,
          margin: '0 auto',
          marginLeft: treeOffsetX > 0 ? treeOffsetX : undefined,
        }}
      >
        {/* SVG edge layer */}
        <svg
          className={styles.edgeLayer}
          width={layout.width}
          height={layout.height + PAD_BOTTOM}
        >
          {layout.edges.map((e, i) => (
            <path
              key={i}
              d={makeEdgePath(e)}
              fill="none"
              stroke="var(--mantine-color-gray-4)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          ))}
        </svg>

        {/* Nodes */}
        {layout.nodes.map((n) => (
          <div
            key={n.id}
            className={styles.nodeWrap}
            style={{ left: n.x, top: n.y, width: n.w, minHeight: n.h }}
          >
            <LayoutNodeRenderer
              node={n}
              onEditTrigger={onEditTrigger}
              onEditBranch={onEditBranch}
              onEditAction={onEditAction}
              onRemoveStep={handleRemoveStep}
              onRemoveBranch={handleRemoveBranch}
              onAddActionAt={handleAddActionAt}
              onAddBranchSplitAt={handleAddBranchSplitAt}
              onAddElseAction={handleAddElseAction}
              onAddElseBranch={handleAddElseBranch}
              onAddRootAction={handleAddRootAction}
              onAddRootBranch={handleAddRootBranchSplit}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Node renderer — dispatches by node kind
// ---------------------------------------------------------------------------

function LayoutNodeRenderer({
  node,
  onEditTrigger,
  onEditBranch,
  onEditAction,
  onRemoveStep,
  onRemoveBranch,
  onAddActionAt,
  onAddBranchSplitAt,
  onAddElseAction,
  onAddElseBranch,
  onAddRootAction,
  onAddRootBranch,
}: {
  node: LayoutRect;
  onEditTrigger: () => void;
  onEditBranch: (branchId: string) => void;
  onEditAction: (actionId: string) => void;
  onRemoveStep: (stepId: string) => void;
  onRemoveBranch: (splitId: string, branchId: string) => void;
  onAddActionAt: (branchId: string, afterStepId: string | null) => void;
  onAddBranchSplitAt: (branchId: string, afterStepId: string | null) => void;
  onAddElseAction: (splitId: string) => void;
  onAddElseBranch: (splitId: string) => void;
  onAddRootAction: () => void;
  onAddRootBranch: () => void;
}): JSX.Element {
  switch (node.kind) {
    case 'trigger':
      return <TriggerNodeInner trigger={node.data as TriggerConfig | undefined} onClick={onEditTrigger} />;

    case 'action': {
      const step = node.data as Extract<WorkflowStep, { kind: 'action' }>;
      return (
        <ActionNode
          action={step.action}
          onClick={() => onEditAction(step.action.id)}
          onRemove={() => onRemoveStep(step.id)}
        />
      );
    }

    case 'branch-card': {
      const d = node.data as { branch: WorkflowBranch; branchIndex: number; splitId: string };
      return (
        <BranchStepCard
          branch={d.branch}
          branchIndex={d.branchIndex}
          onEdit={() => onEditBranch(d.branch.id)}
          onRemove={() => onRemoveBranch(d.splitId, d.branch.id)}
        />
      );
    }

    case 'else-card': {
      return <ElseBranchNode />;
    }

    case 'add-btn': {
      const d = node.data as { branchId?: string; branchSteps?: WorkflowStep[]; splitId?: string; isElse?: boolean; isRoot?: boolean; rootSteps?: WorkflowStep[] };
      if (d.isRoot) {
        return (
          <AddStepMenu
            onAddAction={onAddRootAction}
            onAddBranch={onAddRootBranch}
          />
        );
      }
      if (d.isElse && d.splitId) {
        return (
          <AddStepMenu
            onAddAction={() => onAddElseAction(d.splitId!)}
            onAddBranch={() => onAddElseBranch(d.splitId!)}
          />
        );
      }
      const lastStepId = d.branchSteps && d.branchSteps.length > 0 ? d.branchSteps[d.branchSteps.length - 1].id : null;
      return (
        <AddStepMenu
          onAddAction={() => onAddActionAt(d.branchId!, lastStepId)}
          onAddBranch={() => onAddBranchSplitAt(d.branchId!, lastStepId)}
        />
      );
    }

    default:
      return <></>;
  }
}

// ---------------------------------------------------------------------------
// Node card components (visual only — no positioning responsibility)
// ---------------------------------------------------------------------------

function TriggerNodeInner({ trigger, onClick }: { trigger: TriggerConfig | undefined; onClick: () => void }): JSX.Element {
  if (!trigger) return <></>;
  return <TriggerNode trigger={trigger} onClick={onClick} />;
}

function TriggerNode({ trigger, onClick }: { trigger: TriggerConfig; onClick: () => void }): JSX.Element {
  const resourceLabels = trigger.resourceTypes.map((rt) => TRIGGER_RESOURCE_TYPES.find((t) => t.value === rt)?.label ?? rt).join(', ');
  const eventLabels = trigger.eventKinds.map((ek) => TRIGGER_EVENT_KINDS.find((e) => e.value === ek)?.label ?? ek).join(', ');
  const operations = trigger.operations?.length ? trigger.operations : (trigger.operation ? [trigger.operation] : []);
  const operationLabels = operations.map((operation) => TRIGGER_OPERATIONS.find((entry) => entry.value === operation)?.label ?? operation).join(', ');
  const isEmpty = trigger.resourceTypes.length === 0;
  return (
    <div className={styles.node} onClick={onClick} style={{ width: 380 }}>
      <div className={`${styles.nodeHeader} ${styles.triggerHeader}`}>
        <div className={`${styles.nodeHeaderIcon} ${styles.triggerIcon}`}><IconBolt size={16} /></div>
        <Text size="sm" fw={600}>Trigger</Text>
      </div>
      <div className={styles.nodeBody}>
        {isEmpty ? (
          <Text size="sm" c="gold.6" fw={500}>Click to select a trigger resource and event</Text>
        ) : (
          <>
            <Text size="sm" c="gray.7" fw={500}>{resourceLabels}</Text>
            {(operationLabels || eventLabels) && <Text size="xs" c="dimmed" mt={2}>When: {operationLabels || eventLabels}</Text>}
          </>
        )}
      </div>
    </div>
  );
}

function BranchStepCard({ branch, branchIndex, onEdit, onRemove }: {
  branch: WorkflowBranch; branchIndex: number;
  onEdit: () => void; onRemove: () => void;
}): JSX.Element {
  return (
    <div className={styles.node} onClick={onEdit}>
      <div className={`${styles.nodeHeader} ${styles.branchHeader}`}>
        <div className={`${styles.nodeHeaderIcon} ${styles.branchIcon}`}><IconGitBranch size={14} /></div>
        <Text size="xs" fw={600}>Branch</Text>
        <Box ml="auto" onClick={(e) => e.stopPropagation()}>
          <Menu position="bottom-end" withArrow withinPortal>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="xs" aria-label="Branch menu"><IconTrash size={12} /></ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={onRemove}>Remove branch</Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Box>
      </div>
      <div className={styles.nodeBody}>
        <Group gap={6} mb={6} wrap="nowrap" align="flex-start">
          <Text size="xs" fw={600} c="gray.8" style={{ flex: 1, minWidth: 0 }} lineClamp={2}>{branch.name || `Branch ${branchIndex + 1}`}</Text>
          {branch.alwaysRun && <Badge size="xs" variant="light" color="green">Always run</Badge>}
        </Group>
        {branch.alwaysRun ? (
          <Text size="xs" c="green.7" fw={500}>Runs whenever the trigger fires (no condition filter).</Text>
        ) : (
          <ConditionSummary groups={branch.conditionGroups} />
        )}
      </div>
    </div>
  );
}

function ElseBranchNode(): JSX.Element {
  return (
    <div className={`${styles.node} ${styles.branchStepNodeReadOnly}`}>
      <div className={`${styles.nodeHeader} ${styles.branchHeader}`}>
        <div className={`${styles.nodeHeaderIcon} ${styles.branchIcon}`}><IconGitBranch size={14} /></div>
        <Text size="xs" fw={600}>Else</Text>
      </div>
      <div className={styles.nodeBody}><Text size="xs" c="dimmed">When no branch above matches</Text></div>
    </div>
  );
}

function ActionNode({ action, onClick, onRemove, onAddAction, onAddBranch }: {
  action: RuleAction; onClick: () => void; onRemove: () => void;
  onAddAction?: () => void; onAddBranch?: () => void;
}): JSX.Element {
  const isNotification = action.type === 'send-notification';
  const isFormAssignment = action.type === 'assign-template-forms';
  const actionKeyLabel = RULE_ACTION_KEYS.find((k) => k.value === action.actionKey)?.label;
  const iconClass = isNotification ? styles.actionIconNotify : isFormAssignment ? styles.actionIconTask : '';
  return (
    <div className={styles.node} onClick={onClick}>
      <div className={`${styles.nodeHeader} ${styles.actionHeader}`}>
        <div className={`${styles.nodeHeaderIcon} ${iconClass}`}>
          {isNotification ? <IconBell size={14} /> : <IconClipboardList size={14} />}
        </div>
        <Text size="xs" fw={600}>{ACTION_TYPES.find((t) => t.value === action.type)?.label ?? action.type}</Text>
        <Box ml="auto" onClick={(e) => e.stopPropagation()}>
          <Group gap={4} wrap="nowrap">
            {onAddAction && onAddBranch && <AddStepMenu onAddAction={onAddAction} onAddBranch={onAddBranch} />}
            <ActionIcon variant="subtle" color="red" size="xs" onClick={onRemove}><IconTrash size={12} /></ActionIcon>
          </Group>
        </Box>
      </div>
      <div className={styles.nodeBody}>
        <Text size="xs" c="gray.7">{action.label || actionKeyLabel || 'Click to configure this action'}</Text>
        <ActionNodeDetails action={action} isNotification={isNotification} isFormAssignment={isFormAssignment} />
      </div>
    </div>
  );
}

function ActionNodeDetails({ action, isNotification, isFormAssignment }: { action: RuleAction; isNotification: boolean; isFormAssignment: boolean }): JSX.Element {
  const details: string[] = [];
  if (isNotification) {
    if (action.label) details.push(action.label);
  }
  if (isFormAssignment) {
    if (action.formTemplateRefs?.length) details.push(`Forms: ${action.formTemplateRefs.map((f) => f.title).join(', ')}`);
    if (action.assignmentRecipientPolicies?.length) details.push(`To: ${action.assignmentRecipientPolicies.map((rp) => rp.replace(/-/g, ' ')).join(', ')}`);
    if (action.assignmentDueOffsetDays !== undefined) details.push(`Due in: ${action.assignmentDueOffsetDays} days`);
  }
  if (action.type === 'set-claim-field') {
    if (action.claimFieldPath) details.push(action.claimFieldPath);
    if (action.claimFieldValue) details.push(action.claimFieldValue);
  } else if (action.timingBucket) {
    details.push(`Timing: ${action.timingBucket}`);
  }
  if (details.length === 0) return <></>;
  return (
    <Stack gap={1} mt={4}>
      {details.map((d, i) => <Text key={i} size="xs" c="dimmed" lineClamp={1}>{d}</Text>)}
    </Stack>
  );
}

function ConditionSummary({ groups }: { groups: ConditionGroup[] }): JSX.Element {
  if (groups.length === 0) return <Text size="xs" c="dimmed" fs="italic">No conditions defined</Text>;
  return (
    <Stack gap={2}>
      {groups.map((group) =>
        group.conditions.map((cond, ci) => {
          const fieldLabel = CONDITION_FIELDS.find((f) => f.value === cond.field)?.label ?? cond.field;
          const opLabel = CONDITION_OPERATORS.find((o) => o.value === cond.operator)?.label ?? cond.operator;
          const opts = getConditionValueSelectOptions(cond.field, cond.operator);
          const valueLabel = (opts?.find((o) => o.value === cond.value)?.label) ?? cond.value;
          return (
            <Text key={cond.id || ci} size="xs" c="gray.7">
              <Text span fw={600} c="gray.8">{fieldLabel}</Text>{' '}{opLabel}{' '}
              {cond.value && <Text span fw={600} c="gray.8">{valueLabel}</Text>}
              {ci < group.conditions.length - 1 && <Text span c="dimmed" fw={500}> {group.groupOperator} </Text>}
            </Text>
          );
        }),
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Add step menu
// ---------------------------------------------------------------------------

function AddStepMenu({ onAddAction, onAddBranch }: { onAddAction: () => void; onAddBranch: () => void }): JSX.Element {
  return (
    <Menu position="bottom" withArrow withinPortal>
      <Menu.Target>
        <Tooltip label="Add step">
          <button type="button" className={styles.addBtn}><IconPlus size={14} /></button>
        </Tooltip>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconClipboardList size={14} />} onClick={onAddAction}>Action</Menu.Item>
        <Menu.Item leftSection={<IconGitBranch size={14} />} onClick={onAddBranch}>Branch</Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// Initial placeholder / passthrough (non-tree modes)
// ---------------------------------------------------------------------------

function AddFlowPlaceholder({ onAddActions, onAddConditional }: { onAddActions: () => void; onAddConditional: () => void }): JSX.Element {
  return (
    <div className={styles.initPlaceholder}>
      <div className={styles.terminalNode} style={{ width: 380, cursor: 'pointer', borderColor: 'var(--mantine-color-gold-3)' }} onClick={onAddActions}>
        <Stack align="center" gap={4}>
          <Group justify="center" gap="xs"><IconPlus size={14} /><Text size="sm" fw={500} c="gold.7">Add actions</Text></Group>
          <Text size="xs" c="dimmed">Always run when trigger fires</Text>
        </Stack>
      </div>
      <div className={styles.terminalNode} style={{ width: 380, cursor: 'pointer', borderColor: 'var(--mantine-color-blue-3)' }} onClick={onAddConditional}>
        <Stack align="center" gap={4}>
          <Group justify="center" gap="xs"><IconGitBranch size={14} /><Text size="sm" fw={500} c="blue.7">Add conditional branches</Text></Group>
          <Text size="xs" c="dimmed">Define IF conditions and THEN actions</Text>
        </Stack>
      </div>
    </div>
  );
}

function PassthroughLane({ rung, onEditAction, onAddAction, onAddBranch, onRemoveAction, onAddConditions }: {
  rung: LadderRung; onEditAction: (actionId: string) => void; onAddAction: () => void;
  onAddBranch: () => void; onRemoveAction: (actionId: string) => void; onAddConditions: () => void;
}): JSX.Element {
  return (
    <Stack align="center" gap={0}>
      {rung.actions.map((action, i) => (
        <div key={action.id} style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <ActionNode action={action} onClick={() => onEditAction(action.id)} onRemove={() => onRemoveAction(action.id)} />
          {i < rung.actions.length - 1 && <div style={{ width: 2, height: 40, background: 'var(--mantine-color-gray-4)' }} />}
        </div>
      ))}
      {rung.actions.length > 0 && <div style={{ width: 2, height: 40, background: 'var(--mantine-color-gray-4)' }} />}
      <AddStepMenu onAddAction={onAddAction} onAddBranch={onAddBranch} />
      <Box mt="md">
        <Text size="xs" c="blue.6" fw={500} style={{ cursor: 'pointer' }} onClick={onAddConditions}>
          + Add conditions to filter when this fires
        </Text>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isAlwaysRunRung(rung: LadderRung): boolean {
  if (rung.alwaysRun) return true;
  return rung.conditionGroups.every((g) => g.conditions.length === 0 || g.conditions.every((c) => !c.field && !c.value));
}
